import { useState } from "react";
import { Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUIStates,
  selectUser,
  setSelectedMenu,
  setSelectedMenuId,
  setUser,
  setLinkRecords,
  selectEditingCollection,
  setUIStates,
  TOOLS,
} from "../../../redux/features/appData";
import {
  setMessages,
  setCurrentThread,
  setPrompt,
  setInitialLoading,
  setEmployees,
  setPersonas,
  loadRecentSelectionsForWorkspace,
} from "../../../redux/features/agent/agentSlice";
import { MENUS } from "../../config/menus";
import { useCoreDataLoader } from "../../../hooks/useCoreDataLoader";
import { apiSetCurrentWorkspace } from "../../../api";
import { Building2, ChevronRight, Plus } from "lucide-react";
import ModalAddWorkspace from "../../workspace-picker/ModalAddWorkspace";

const AVATAR_MENU = {
  LOGOUT: "logout",
  PERSONAL: "personal",
  WORKSPACE: "workspace",
  BILLING: "billing",
  GMAIL: "gmail",
  WORKSPACE_SWITCH: "workspace_switch",
  ADD_WORKSPACE: "add_workspace",
};

type WazAvatarMenuProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
};

export const WazAvatarMenu = ({ anchorEl, onClose }: WazAvatarMenuProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const uiStates = useSelector(selectUIStates);
  const storeUser = useSelector(selectUser);
  const currentCollectionId = useSelector(selectEditingCollection)?.id || "";

  const { reloadAll } = useCoreDataLoader();

  const [workspaceSubmenuAnchor, setWorkspaceSubmenuAnchor] =
    useState<HTMLElement | null>(null);
  const [showAddWorkspaceModal, setShowAddWorkspaceModal] = useState(false);

  const handleMenuSelection = (selection: string) => {
    onClose();

    switch (selection) {
      case AVATAR_MENU.PERSONAL:
        dispatch(setUIStates({ ...uiStates, selectedTool: TOOLS.SETTINGS }));
        dispatch(setSelectedMenu(MENUS.UNSELECTED));
        dispatch(setSelectedMenuId(undefined));
        navigate("/waz/settings/personal");
        break;
      case AVATAR_MENU.WORKSPACE:
        dispatch(setUIStates({ ...uiStates, selectedTool: TOOLS.SETTINGS }));
        dispatch(setSelectedMenu(MENUS.UNSELECTED));
        dispatch(setSelectedMenuId(undefined));
        navigate("/waz/settings/workspaces");
        break;
      // case AVATAR_MENU.BILLING:
      //     dispatch(setSelectedMenu(MENUS.UNSELECTED));
      //     dispatch(setSelectedMenuId(undefined));
      //     navigate("/waz/billing");
      //     break;
      case AVATAR_MENU.LOGOUT:
        navigate("/logout");
        break;
      // case AVATAR_MENU.GMAIL:
      //     navigate("/plugins");
      //     break;
      default:
    }
  };

  const handleWorkspaceClick = (event: React.MouseEvent<HTMLElement>) => {
    setWorkspaceSubmenuAnchor(event.currentTarget);
  };

  const handleWorkspaceSubmenuClose = () => {
    setWorkspaceSubmenuAnchor(null);
    onClose();
  };

  const handleWorkspaceSwitch = async (workspaceId: string) => {
    if (workspaceId === "__new") {
      // dispatch(setUIStates({...uiStates, selectedTool: TOOLS.SETTINGS}));
      setShowAddWorkspaceModal(true);
      handleWorkspaceSubmenuClose();
      return;
    }

    // Set new workspace ID
    dispatch(setUIStates({ ...uiStates, selectedTool: TOOLS.CHAT }));
    dispatch(setSelectedMenu(MENUS.HOME));
    dispatch(setSelectedMenuId(undefined));

    // Clear agent state before switching workspaces
    dispatch(setEmployees([])); // Clear FIRST
    dispatch(setPersonas([])); // Clear FIRST
    dispatch(setMessages([])); // Then clear messages
    dispatch(setCurrentThread(null));
    dispatch(setPrompt(""));
    dispatch(setInitialLoading(true));

    // Load recent selections for the new workspace
    dispatch(loadRecentSelectionsForWorkspace(workspaceId));

    // Reload all data for the new workspace
    const newCollectionId = await reloadAll(workspaceId);

    // Update user's current workspace
    if (storeUser) {
      const newUser = {
        ...storeUser,
        currentWorkspaceId: workspaceId,
      };
      dispatch(setUser(newUser));
      await apiSetCurrentWorkspace(storeUser.id, workspaceId);
    }

    // Navigate to waz home since this is coming from WazAvatarMenu
    navigate("/waz/chat/" + (newCollectionId || currentCollectionId));
    handleWorkspaceSubmenuClose();
  };

  const handleAddWorkspaceSuccess = (newCollectionId?: string) => {
    setShowAddWorkspaceModal(false);
    // The modal will handle updating the user state and reloading data
    // Navigate to waz home after successful workspace creation
    navigate("/waz/chat/" + (newCollectionId || currentCollectionId));
    handleWorkspaceSubmenuClose();
  };

  const getCurrentWorkspaceName = () => {
    if (!storeUser?.workspaces) return "Workspace";
    const currentWorkspace = storeUser.workspaces.find(
      (w) => w.id === storeUser.currentWorkspaceId
    );
    return currentWorkspace?.name || "Workspace";
  };

  const getWorkspaceOptions = () => {
    if (!storeUser?.workspaces) return [];

    const workspacesCopy = [...storeUser.workspaces];
    workspacesCopy.sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );

    const options = workspacesCopy.map((workspace) => ({
      value: workspace.id,
      label: workspace.name,
      isCurrent: workspace.id === storeUser.currentWorkspaceId,
    }));

    // Add the "add new workspace" option
    options.push({
      value: "__new",
      label: "Add New Workspace",
      isCurrent: false,
    });

    return options;
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}>
        {/* Current Workspace with Submenu */}
        <MenuItem onClick={handleWorkspaceClick}>
          <ListItemIcon>
            <Building2 size={16} />
          </ListItemIcon>
          <ListItemText primary={getCurrentWorkspaceName()} />
          <ChevronRight size={16} />
        </MenuItem>

        <MenuItem onClick={() => handleMenuSelection(AVATAR_MENU.WORKSPACE)}>
          Workspace Settings
        </MenuItem>
        <MenuItem onClick={() => handleMenuSelection(AVATAR_MENU.PERSONAL)}>
          Personal Settings
        </MenuItem>
        <MenuItem onClick={() => handleMenuSelection(AVATAR_MENU.LOGOUT)}>
          <span style={{ color: "var(--text-color-error)" }}>Log Out</span>
        </MenuItem>
      </Menu>

      {/* Workspace Submenu */}
      <Menu
        anchorEl={workspaceSubmenuAnchor}
        open={Boolean(workspaceSubmenuAnchor)}
        onClose={handleWorkspaceSubmenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}>
        {getWorkspaceOptions().map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleWorkspaceSwitch(option.value)}
            selected={option.isCurrent}
            disabled={option.isCurrent}>
            {option.value === "__new" ? (
              <>
                <ListItemIcon>
                  <Plus size={16} />
                </ListItemIcon>
                <ListItemText primary={option.label} />
              </>
            ) : (
              <>
                <ListItemIcon>
                  <Building2 size={16} />
                </ListItemIcon>
                <ListItemText
                  primary={option.label}
                  primaryTypographyProps={{
                    style: {
                      fontWeight: option.isCurrent ? "bold" : "normal",
                      color: option.isCurrent
                        ? "var(--text-color-primary)"
                        : "var(--text-color-secondary)",
                    },
                  }}
                />
              </>
            )}
          </MenuItem>
        ))}
      </Menu>

      {/* Add Workspace Modal */}
      <ModalAddWorkspace
        isOpen={showAddWorkspaceModal}
        onClose={() => setShowAddWorkspaceModal(false)}
        onSuccess={handleAddWorkspaceSuccess}
      />
    </>
  );
};
function setSelectedTool(CHAT: any): any {
  throw new Error("Function not implemented.");
}
